import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaUser, FaIdCard, FaPhone, FaEnvelope, FaCity, FaCalendarAlt, FaUsers, FaChild, FaHome } from 'react-icons/fa';
import { supabase } from '../lib/supabaseClient';

const QuoteModal = ({ isOpen, onClose, existingQuote = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    dni: '',
    phone: '',
    email: '',
    city: '',
    checkin: '',
    checkout: '',
    guests: '1',
    children: '0',
    status: 'pending',
    price: '',
    notes: '',
    services_included: 'Todas las habitaciones, Uso exclusivo de instalaciones, Limpieza diaria'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (existingQuote) {
      setFormData({
        name: existingQuote.full_name || '',
        dni: existingQuote.dni || '',
        phone: existingQuote.phone || '',
        email: existingQuote.email || '',
        city: existingQuote.city_of_origin || '',
        checkin: existingQuote.checkin_date || '',
        checkout: existingQuote.checkout_date || '',
        guests: existingQuote.number_of_guests?.toString() || '1',
        children: existingQuote.number_of_children?.toString() || '0',
        status: existingQuote.status || 'pending',
        price: existingQuote.price?.toString() || '',
        notes: existingQuote.notes || '',
        services_included: existingQuote.services_included || 'Todas las habitaciones, Uso exclusivo de instalaciones, Limpieza diaria'
      });
    } else {
      setFormData({
        name: '',
        dni: '',
        phone: '',
        email: '',
        city: '',
        checkin: '',
        checkout: '',
        guests: '1',
        children: '0',
        status: 'pending',
        price: '',
        notes: '',
        services_included: 'Todas las habitaciones, Uso exclusivo de instalaciones, Limpieza diaria'
      });
    }
  }, [existingQuote, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const quoteData = {
        full_name: formData.name,
        dni: formData.dni,
        phone: formData.phone,
        email: formData.email,
        city_of_origin: formData.city,
        checkin_date: formData.checkin,
        checkout_date: formData.checkout,
        number_of_guests: parseInt(formData.guests),
        number_of_children: parseInt(formData.children),
        status: formData.status,
        price: formData.price ? parseFloat(formData.price) : null,
        notes: formData.notes,
        services_included: formData.services_included
      };

      if (existingQuote) {
        // Actualizar cotización existente
        const { error } = await supabase
          .from('full_house_quotes')
          .update(quoteData)
          .eq('id', existingQuote.id);

        if (error) throw error;
        alert('Cotización actualizada exitosamente');
      } else {
        // Crear nueva cotización
        const { error } = await supabase
          .from('full_house_quotes')
          .insert([quoteData]);

        if (error) throw error;
        alert('Cotización creada exitosamente');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error al guardar cotización:', error);
      alert('Error al guardar la cotización: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateNights = () => {
    if (!formData.checkin || !formData.checkout) return 0;
    const checkinDate = new Date(formData.checkin);
    const checkoutDate = new Date(formData.checkout);
    const timeDiff = checkoutDate.getTime() - checkinDate.getTime();
    const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return dayDiff > 0 ? dayDiff : 0;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black bg-opacity-80 z-[99999] flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white p-6 border-b border-gray-200 rounded-t-xl z-10">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 p-2"
              >
                <FaTimes size={20} />
              </button>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FaHome className="text-green-600" />
                {existingQuote ? 'Editar Cotización' : 'Nueva Cotización de Casa Completa'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {existingQuote ? 'Actualiza los datos de la cotización' : 'Completa los datos del cliente'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Información Personal */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
                    <div className="w-1 h-5 bg-blue-500 rounded"></div>
                    Información Personal
                  </h3>

                  <div>
                    <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-700">
                      Nombre Completo *
                    </label>
                    <div className="relative">
                      <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="dni" className="block mb-2 text-sm font-medium text-gray-700">
                      DNI *
                    </label>
                    <div className="relative">
                      <FaIdCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        id="dni"
                        name="dni"
                        value={formData.dni}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-700">
                      Teléfono *
                    </label>
                    <div className="relative">
                      <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700">
                      Email *
                    </label>
                    <div className="relative">
                      <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="city" className="block mb-2 text-sm font-medium text-gray-700">
                      Ciudad de Origen *
                    </label>
                    <div className="relative">
                      <FaCity className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Información de Estadía */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
                    <div className="w-1 h-5 bg-purple-500 rounded"></div>
                    Información de Estadía
                  </h3>

                  <div>
                    <label htmlFor="checkin" className="block mb-2 text-sm font-medium text-gray-700">
                      Fecha de Entrada *
                    </label>
                    <div className="relative">
                      <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        id="checkin"
                        name="checkin"
                        value={formData.checkin}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="checkout" className="block mb-2 text-sm font-medium text-gray-700">
                      Fecha de Salida *
                    </label>
                    <div className="relative">
                      <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        id="checkout"
                        name="checkout"
                        value={formData.checkout}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  {calculateNights() > 0 && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <p className="text-center text-lg font-semibold text-blue-800">
                        {calculateNights()} {calculateNights() === 1 ? 'noche' : 'noches'}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="guests" className="block mb-2 text-sm font-medium text-gray-700">
                        Nº Adultos *
                      </label>
                      <div className="relative">
                        <FaUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="number"
                          id="guests"
                          name="guests"
                          value={formData.guests}
                          onChange={handleChange}
                          min="1"
                          required
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="children" className="block mb-2 text-sm font-medium text-gray-700">
                        Nº Niños
                      </label>
                      <div className="relative">
                        <FaChild className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="number"
                          id="children"
                          name="children"
                          value={formData.children}
                          onChange={handleChange}
                          min="0"
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="status" className="block mb-2 text-sm font-medium text-gray-700">
                      Estado
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="pending">Pendiente</option>
                      <option value="confirmed">Confirmada</option>
                      <option value="cancelled">Cancelada</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="price" className="block mb-2 text-sm font-medium text-gray-700">
                      Precio Total (S/.) *
                    </label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="Ej: 1500.00"
                      step="0.01"
                      min="0"
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Precio por toda la estadía</p>
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="notes" className="block mb-2 text-sm font-medium text-gray-700">
                      Notas / Observaciones
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Información adicional, condiciones especiales, etc..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
              </div>

              {/* Sección adicional para servicios */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <div className="w-1 h-5 bg-green-500 rounded"></div>
                  Servicios Incluidos
                </h3>
                <textarea
                  id="services_included"
                  name="services_included"
                  value={formData.services_included}
                  onChange={handleChange}
                  rows="3"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-2">Separa cada servicio con una coma o en líneas diferentes</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Guardando...' : existingQuote ? 'Actualizar' : 'Crear Cotización'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuoteModal;
